/**
 * Training Materials Service
 * Admin can upload videos, PDFs, docs, images
 * All users can view/stream materials
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { uploadFileToStorage, deleteFileFromStorage } from './storageService';
import type { DocumentAttachment } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export type TrainingMaterialType = 'video' | 'pdf' | 'doc' | 'image' | 'other';
export type TrainingStatus = 'new' | 'popular' | 'essential';
export type TrainingCategory = 'onboarding' | 'product' | 'sales' | 'compliance' | 'technical' | 'general';

export interface TrainingMaterial {
  id: string;
  title: string;
  description: string;
  type: TrainingMaterialType;
  category: TrainingCategory;
  status: TrainingStatus;
  duration?: string; // e.g., "15 min" or "20 pages"
  fileUrl: string;
  thumbnailUrl?: string;
  fileName: string;
  fileSize: number;
  viewCount: number;
  uploadedBy: string;
  uploadedByName: string;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const materialsCollection = collection(db, 'training_materials');

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return timestamp.toDate();
  }
  return new Date();
};

export const getMaterialType = (_fileName: string, mimeType: string): TrainingMaterialType => {
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document') || 
      mimeType.includes('presentation') || mimeType.includes('spreadsheet')) return 'doc';
  if (mimeType.startsWith('image/')) return 'image';
  return 'other';
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
};

// ============================================================================
// TRAINING MATERIALS
// ============================================================================

/**
 * Get all training materials
 */
export async function getAllTrainingMaterials(): Promise<TrainingMaterial[]> {
  try {
    const q = query(materialsCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        status: data.status || 'new',
        duration: data.duration,
        fileUrl: data.fileUrl,
        thumbnailUrl: data.thumbnailUrl,
        fileName: data.fileName,
        fileSize: data.fileSize,
        viewCount: data.viewCount || 0,
        uploadedBy: data.uploadedBy,
        uploadedByName: data.uploadedByName,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as TrainingMaterial;
    });
  } catch (error) {
    console.error('Error fetching training materials:', error);
    throw error;
  }
}

/**
 * Get training materials by category
 */
export async function getTrainingMaterialsByCategory(
  category: TrainingCategory
): Promise<TrainingMaterial[]> {
  try {
    const q = query(
      materialsCollection,
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        status: data.status || 'new',
        duration: data.duration,
        fileUrl: data.fileUrl,
        thumbnailUrl: data.thumbnailUrl,
        fileName: data.fileName,
        fileSize: data.fileSize,
        viewCount: data.viewCount || 0,
        uploadedBy: data.uploadedBy,
        uploadedByName: data.uploadedByName,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as TrainingMaterial;
    });
  } catch (error) {
    console.error('Error fetching materials by category:', error);
    throw error;
  }
}

/**
 * Get training materials by type
 */
export async function getTrainingMaterialsByType(
  type: TrainingMaterialType
): Promise<TrainingMaterial[]> {
  try {
    const q = query(
      materialsCollection,
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        status: data.status || 'new',
        duration: data.duration,
        fileUrl: data.fileUrl,
        thumbnailUrl: data.thumbnailUrl,
        fileName: data.fileName,
        fileSize: data.fileSize,
        viewCount: data.viewCount || 0,
        uploadedBy: data.uploadedBy,
        uploadedByName: data.uploadedByName,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as TrainingMaterial;
    });
  } catch (error) {
    console.error('Error fetching materials by type:', error);
    throw error;
  }
}

/**
 * Get single training material
 */
export async function getTrainingMaterial(materialId: string): Promise<TrainingMaterial | null> {
  try {
    const docRef = doc(materialsCollection, materialId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        title: data.title,
        description: data.description,
        type: data.type,
        category: data.category,
        status: data.status || 'new',
        duration: data.duration,
        fileUrl: data.fileUrl,
        thumbnailUrl: data.thumbnailUrl,
        fileName: data.fileName,
        fileSize: data.fileSize,
        viewCount: data.viewCount || 0,
        uploadedBy: data.uploadedBy,
        uploadedByName: data.uploadedByName,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as TrainingMaterial;
    }
    return null;
  } catch (error) {
    console.error('Error fetching training material:', error);
    return null;
  }
}

/**
 * Upload a training file to Firebase Storage (Admin only).
 * Storage path: training/{timestamp}-{filename}
 * Returns a DocumentAttachment — compatible with useFileUpload's executeUploads().
 */
export async function uploadTrainingAttachment(
  file: File,
  _userId: string
): Promise<DocumentAttachment> {
  return uploadFileToStorage(file, `training/${Date.now()}-${file.name}`);
}

/**
 * Create training material (Admin only)
 */
export async function createTrainingMaterial(
  materialData: Omit<TrainingMaterial, 'id' | 'viewCount' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    const now = Timestamp.now();
    
    // Filter out undefined values (Firestore doesn't accept them)
    const cleanData = Object.entries(materialData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);
    
    const docRef = await addDoc(materialsCollection, {
      ...cleanData,
      viewCount: 0,
      createdAt: now,
      updatedAt: now,
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating training material:', error);
    throw error;
  }
}

/**
 * Update training material (Admin only)
 */
export async function updateTrainingMaterial(
  materialId: string,
  updates: Partial<Omit<TrainingMaterial, 'id' | 'viewCount' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  try {
    const docRef = doc(materialsCollection, materialId);
    
    // Filter out undefined values (Firestore doesn't accept them)
    const cleanUpdates = Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);
    
    await updateDoc(docRef, {
      ...cleanUpdates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating training material:', error);
    throw error;
  }
}

/**
 * Delete training material (Admin only)
 */
export async function deleteTrainingMaterial(materialId: string): Promise<void> {
  try {
    const docRef = doc(materialsCollection, materialId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();

      // Delete main file from storage
      if (data.fileUrl) await deleteFileFromStorage(data.fileUrl);

      // Delete thumbnail if present (legacy records)
      if (data.thumbnailUrl) await deleteFileFromStorage(data.thumbnailUrl);

      // Delete Firestore document
      await deleteDoc(docRef);
    }
  } catch (error) {
    console.error('Error deleting training material:', error);
    throw error;
  }
}

/**
 * Increment view count when user opens material
 */
export async function incrementViewCount(materialId: string): Promise<void> {
  try {
    const docRef = doc(materialsCollection, materialId);
    await updateDoc(docRef, {
      viewCount: increment(1),
    });
  } catch (error) {
    console.error('Error incrementing view count:', error);
    // Don't throw - view count is not critical
  }
}

/**
 * Export utility function for formatting file size
 */
export { formatFileSize };
